import { parentSupabase } from "../utils/supabaseClient";
import { RealtimeChannel } from "@supabase/supabase-js";
import axios from "./axiosInstance";
import { getAuthToken } from "./apiHelpers";
import { captureException } from "../utils/sentry";

// API base URL
const BASE_URL = process.env.REACT_APP_API_URL || "";

// Helper to get headers for API calls
const getApiHeaders = () => {
  const token = getAuthToken();
  return {
    "Content-Type": "application/json",
    authorization: token ? `Bearer ${token}` : "",
  };
};

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface ChatConversation {
  id: string;
  type: "dm" | "channel" | "support";
  scope: "tenant" | "platform";
  tenant_id: string | null;
  name: string | null;
  description: string | null;
  is_role_based: boolean;
  role_filter: string[];
  status: "active" | "archived" | "closed";
  created_by: string;
  created_at: string;
  updated_at: string;
  // Joined data
  participants?: ChatParticipant[];
  last_message?: ChatMessage;
  unread_count?: number;
}

export interface ChatParticipant {
  id: string;
  conversation_id: string;
  user_id: string;
  user_type: "platform_user" | "tenant_user";
  tenant_id: string | null;
  display_name: string | null;
  role: "member" | "admin" | "owner";
  joined_at: string;
  last_read_at: string;
  is_muted: boolean;
  is_typing: boolean;
  typing_updated_at: string | null;
}

export interface ChatMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  sender_type: "platform_user" | "tenant_user" | "system";
  message_type: "text" | "image" | "file" | "system";
  content: string | null;
  attachments: any[];
  reply_to_id: string | null;
  created_at: string;
  edited_at: string | null;
  is_deleted: boolean;
  // Joined data
  sender?: ChatParticipant;
}

export interface UserInfo {
  id: string;
  type: "platform_user" | "tenant_user";
  tenant_id: string | null;
  display_name: string;
}

// ============================================
// CONVERSATION FUNCTIONS
// ============================================

/**
 * Get all conversations for a user
 */
export const getUserConversations = async (
  userId: string,
  tenantId?: string
): Promise<{ success: boolean; data: ChatConversation[]; error?: string }> => {
  try {
    const response = await axios.get(
      `${BASE_URL}/chat/conversations/${userId}`,
      {
        headers: getApiHeaders(),
        params: tenantId ? { tenantId } : undefined,
      }
    );

    return {
      success: response.data.success,
      data: response.data.data || [],
      error: response.data.error,
    };
  } catch (error: any) {
    return {
      success: false,
      data: [],
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Get or create a DM conversation between two users
 */
export const getOrCreateDMConversation = async (
  currentUser: UserInfo,
  otherUser: UserInfo,
  tenantId?: string
): Promise<{ success: boolean; data?: ChatConversation; error?: string }> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/chat/dm`,
      {
        currentUser,
        otherUser,
        tenantId,
      },
      {
        headers: getApiHeaders(),
      }
    );

    return {
      success: response.data.success,
      data: response.data.data,
      error: response.data.error,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Get a single conversation by ID
 */
export const getConversation = async (
  conversationId: string
): Promise<{ success: boolean; data?: ChatConversation; error?: string }> => {
  try {
    const { data, error } = await parentSupabase
      .from("chat_conversations")
      .select(`
        *,
        participants:chat_participants(*)
      `)
      .eq("id", conversationId)
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    captureException(error);
    return { success: false, error: error.message };
  }
};

// ============================================
// MESSAGE FUNCTIONS
// ============================================

/**
 * Get messages for a conversation
 */
export const getConversationMessages = async (
  conversationId: string,
  limit: number = 50,
  before?: string
): Promise<{ success: boolean; data: ChatMessage[]; error?: string }> => {
  try {
    const params: Record<string, any> = { limit };
    if (before) params.before = before;

    const response = await axios.get(
      `${BASE_URL}/chat/messages/${conversationId}`,
      {
        headers: getApiHeaders(),
        params,
      }
    );

    return {
      success: response.data.success,
      data: response.data.data || [],
      error: response.data.error,
    };
  } catch (error: any) {
    return {
      success: false,
      data: [],
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Send a message to a conversation
 */
export const sendMessage = async (
  conversationId: string,
  senderId: string,
  senderType: "platform_user" | "tenant_user",
  content: string,
  messageType: "text" | "image" | "file" = "text",
  attachments: any[] = [],
  replyToId?: string
): Promise<{ success: boolean; data?: ChatMessage; error?: string }> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/chat/messages/${conversationId}`,
      {
        senderId,
        senderType,
        content,
        messageType,
        attachments,
        replyToId,
      },
      {
        headers: getApiHeaders(),
      }
    );

    return {
      success: response.data.success,
      data: response.data.data,
      error: response.data.error,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Mark messages as read (update last_read_at)
 */
export const markConversationRead = async (
  conversationId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/chat/conversations/${conversationId}/read`,
      { userId },
      { headers: getApiHeaders() }
    );

    return {
      success: response.data.success,
      error: response.data.error,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// ============================================
// TYPING INDICATOR FUNCTIONS
// ============================================

/**
 * Update typing status for a user in a conversation
 */
export const updateTypingStatus = async (
  conversationId: string,
  userId: string,
  isTyping: boolean
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/chat/conversations/${conversationId}/typing`,
      { userId, isTyping },
      { headers: getApiHeaders() }
    );

    return {
      success: response.data.success,
      error: response.data.error,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

// ============================================
// USER LOOKUP FUNCTIONS
// ============================================

/**
 * Get users available for chat in a tenant
 * This queries the tenant's Supabase for members/staff
 */
export const getTenantUsers = async (
  tenantSupabase: any,
  searchQuery?: string
): Promise<{ success: boolean; data: any[]; error?: string }> => {
  try {
    // There is no `members` table — login-capable identities live in `users`,
    // which carries every column selected here. PostgREST rejects the whole
    // request for an unknown table, so this has always returned an error and
    // the chat picker has never listed anyone.
    //
    // `role` is the drifted text column; that is fine for a display label, but
    // do not use it for a permission decision (those resolve from role_id).
    let query = tenantSupabase
      .from("users")
      .select("id, first_name, last_name, email, avatar_url, role")
      .eq("status", "active")
      .limit(50);

    if (searchQuery) {
      query = query.or(
        `first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%`
      );
    }

    const { data, error } = await query;

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error: any) {
    captureException(error);
    return { success: false, data: [], error: error.message };
  }
};

// ============================================
// REALTIME SUBSCRIPTIONS
// ============================================
// DISABLED: Realtime subscriptions on the central/parent DB were exhausting the
// PostgreSQL connection pool, causing full site outages (HTTP 522 on all REST queries).
// Chat still works via REST API polling. Re-enable only after moving chat to tenant DBs
// or implementing proper connection pooling on the central DB.

/**
 * Subscribe to new messages in a conversation
 * DISABLED - returns a no-op channel to avoid central DB connection exhaustion
 */
export const subscribeToMessages = (
  conversationId: string,
  onMessage: (message: ChatMessage) => void
): RealtimeChannel => {
  return parentSupabase.channel(`chat_messages:${conversationId}`);
};

/**
 * Subscribe to conversation updates (status changes, new participants)
 * DISABLED - returns a no-op channel to avoid central DB connection exhaustion
 */
export const subscribeToConversation = (
  conversationId: string,
  onUpdate: (conversation: Partial<ChatConversation>) => void
): RealtimeChannel => {
  return parentSupabase.channel(`chat_conversation:${conversationId}`);
};

/**
 * Subscribe to typing indicators in a conversation
 * DISABLED - returns a no-op channel to avoid central DB connection exhaustion
 */
export const subscribeToTyping = (
  conversationId: string,
  currentUserId: string,
  onTypingChange: (participants: ChatParticipant[]) => void
): RealtimeChannel => {
  return parentSupabase.channel(`chat_typing:${conversationId}`);
};

/**
 * Subscribe to all user's conversations for new messages
 * DISABLED - returns a no-op channel to avoid central DB connection exhaustion
 */
export const subscribeToUserConversations = (
  userId: string,
  onNewMessage: (conversationId: string, message: ChatMessage) => void,
  onConversationUpdate: (conversation: Partial<ChatConversation>) => void
): RealtimeChannel => {
  return parentSupabase.channel(`user_chats:${userId}`);
};

/**
 * Unsubscribe from a channel
 */
export const unsubscribe = (channel: RealtimeChannel): void => {
  parentSupabase.removeChannel(channel);
};

// ============================================
// SUPPORT CHAT FUNCTIONS (for platform support)
// ============================================

/**
 * Support availability response
 */
export interface SupportAvailabilityResponse {
  available: boolean;           // Are agents actively online (within last 5 min)?
  markedAvailable: boolean;     // Are any agents marked as available?
  activeAgentCount: number;     // Number of actively online agents
  markedAgentCount: number;     // Number of agents marked available
  suggestTicket: boolean;       // Should we suggest creating a ticket instead?
}

/**
 * Check if any support agents are available
 * Returns both "actively online" status and "marked available" status
 */
export const checkSupportAvailability = async (): Promise<SupportAvailabilityResponse> => {
  try {
    const response = await axios.get(
      `${BASE_URL}/chat/support/availability`,
      { headers: getApiHeaders() }
    );

    return {
      available: response.data.available || false,
      markedAvailable: response.data.markedAvailable || false,
      activeAgentCount: response.data.activeAgentCount || 0,
      markedAgentCount: response.data.markedAgentCount || 0,
      suggestTicket: response.data.suggestTicket || false,
    };
  } catch (error) {
    return {
      available: false,
      markedAvailable: false,
      activeAgentCount: 0,
      markedAgentCount: 0,
      suggestTicket: true,
    };
  }
};

// Valid support categories
export type SupportCategory = 'billing' | 'technical' | 'account' | 'feature' | 'bug' | 'other';

export interface SupportChatRouting {
  routeTo: 'platform' | 'tenant';
  category: SupportCategory;
  staffCount: number;
}

/**
 * Start a support chat session with category-based routing
 */
export const startSupportChat = async (
  user: UserInfo,
  tenantId: string,
  category: SupportCategory = 'technical'
): Promise<{ success: boolean; data?: ChatConversation; routing?: SupportChatRouting; error?: string }> => {
  try {
    // The tenant UUID is no longer sent — the backend resolves the central
    // tenant record (and its routing config) from the x-tenant-id slug header.
    const response = await axios.post(
      `${BASE_URL}/chat/support/start`,
      { user, tenantId, category },
      { headers: getApiHeaders() }
    );

    return {
      success: response.data.success,
      data: response.data.data,
      routing: response.data.routing,
      error: response.data.error,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Archive or unarchive a conversation
 */
export const archiveConversation = async (
  conversationId: string,
  userId: string,
  archive: boolean = true
): Promise<{ success: boolean; data?: { conversationId: string; isArchived: boolean }; error?: string }> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/chat/conversations/${conversationId}/archive`,
      { userId, archive },
      { headers: getApiHeaders() }
    );

    return {
      success: response.data.success,
      data: response.data.data,
      error: response.data.error,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Get archived conversations for a user
 */
export const getArchivedConversations = async (
  userId: string,
  tenantId?: string
): Promise<{ success: boolean; data: ChatConversation[]; error?: string }> => {
  try {
    const params: Record<string, string> = { includeArchived: "only" };
    if (tenantId) {
      params.tenantId = tenantId;
    }

    const response = await axios.get(
      `${BASE_URL}/chat/conversations/${userId}`,
      {
        headers: getApiHeaders(),
        params,
      }
    );

    return {
      success: response.data.success,
      data: response.data.data || [],
      error: response.data.error,
    };
  } catch (error: any) {
    return {
      success: false,
      data: [],
      error: error.response?.data?.error || error.message,
    };
  }
};

// ============================================
// SUPPORT CATEGORY MANAGEMENT
// ============================================

export interface TenantSupportSettings {
  tenantUuid: string;
  tenantName: string;
  comprehensiveSupport: boolean;
}

export interface CategoryStaffMember {
  id: string;
  tenant_uuid: string;
  category: SupportCategory;
  staff_member_id: string;
  staff_display_name: string | null;
  staff_email: string | null;
  is_owner: boolean;
  created_at: string;
  updated_at: string;
}

export interface CategoryStaffByCategory {
  billing: CategoryStaffMember[];
  technical: CategoryStaffMember[];
  account: CategoryStaffMember[];
  feature: CategoryStaffMember[];
  bug: CategoryStaffMember[];
  other: CategoryStaffMember[];
}

/**
 * Get tenant's support settings
 */
export const getTenantSupportSettings = async (
  tenantUuid: string
): Promise<{ success: boolean; data?: TenantSupportSettings; error?: string }> => {
  try {
    const response = await axios.get(
      `${BASE_URL}/support-categories/settings/${tenantUuid}`,
      { headers: getApiHeaders() }
    );

    return {
      success: response.data.success,
      data: response.data.data,
      error: response.data.error,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Update tenant's support settings
 */
export const updateTenantSupportSettings = async (
  tenantUuid: string,
  comprehensiveSupport: boolean
): Promise<{ success: boolean; data?: TenantSupportSettings; error?: string }> => {
  try {
    const response = await axios.put(
      `${BASE_URL}/support-categories/settings/${tenantUuid}`,
      { comprehensiveSupport },
      { headers: getApiHeaders() }
    );

    return {
      success: response.data.success,
      data: response.data.data,
      error: response.data.error,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Get staff assigned to support categories for a tenant
 */
export const getCategoryStaff = async (
  tenantUuid: string
): Promise<{ success: boolean; data?: { all: CategoryStaffMember[]; byCategory: CategoryStaffByCategory }; error?: string }> => {
  try {
    const response = await axios.get(
      `${BASE_URL}/support-categories/staff/${tenantUuid}`,
      { headers: getApiHeaders() }
    );

    return {
      success: response.data.success,
      data: response.data.data,
      error: response.data.error,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Add a staff member to a support category
 * Accepts tenant slug - backend will resolve to tenant_uuid
 */
export const addCategoryStaff = async (
  tenantSlug: string,
  category: SupportCategory,
  staffMemberId: string,
  staffDisplayName?: string,
  staffEmail?: string,
  isOwner: boolean = false
): Promise<{ success: boolean; data?: CategoryStaffMember; error?: string }> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/support-categories/staff`,
      {
        tenantSlug,
        category,
        staffMemberId,
        staffDisplayName,
        staffEmail,
        isOwner,
      },
      { headers: getApiHeaders() }
    );

    return {
      success: response.data.success,
      data: response.data.data,
      error: response.data.error,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Remove a staff member from a support category
 */
export const removeCategoryStaff = async (
  id: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/support-categories/staff/${id}`,
      { headers: getApiHeaders() }
    );

    return {
      success: response.data.success,
      error: response.data.error,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Initialize default category staff for a tenant (adds owner to all categories)
 * Accepts either tenantUuid or tenantSlug - backend will resolve slug to uuid
 */
export const initializeCategoryStaff = async (
  tenantUuidOrSlug: string,
  ownerId: string,
  ownerDisplayName?: string,
  ownerEmail?: string,
  isSlug: boolean = false
): Promise<{ success: boolean; data?: CategoryStaffMember[]; alreadyInitialized?: boolean; error?: string }> => {
  try {
    const body = isSlug
      ? { tenantSlug: tenantUuidOrSlug, ownerId, ownerDisplayName, ownerEmail }
      : { tenantUuid: tenantUuidOrSlug, ownerId, ownerDisplayName, ownerEmail };

    const response = await axios.post(
      `${BASE_URL}/support-categories/staff/initialize`,
      body,
      { headers: getApiHeaders() }
    );

    return {
      success: response.data.success,
      data: response.data.data,
      alreadyInitialized: response.data.alreadyInitialized,
      error: response.data.error,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};
